// NotificationsMobile.jsx — module Notifications mobile (orchestrateur).
//
// Écran complet : en-tête (retour, titre, compteur non-lus, tout marquer lu,
// options), filtres par type, liste groupée (date / type / aucun), geste de
// swipe, modal de détail (feuille ou centré), feuille d'options et toast
// (avec annulation de suppression). Tout est cloisonné sous .enf.
//
// Intégration minimale :
//   import NotificationsMobile from "./mobile/notifications/NotificationsMobile";
//   <NotificationsMobile
//      notifications={mesNotifs}                 // optionnel — sinon démo
//      onBack={() => navigate(-1)}
//      onOpenEntity={(n) => navigate(n.cta.to)}  // CTA principal
//      onMarkRead={(n, read) => api.setRead(n.id, read)}
//      onMarkAllRead={() => api.markAllRead()}
//      onDelete={(n) => api.remove(n.id)}
//   />
// Charge notifications-mobile.css (voir README).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Ico, FILTERS, BUCKETS } from "./notificationsShared";
import { getDemoNotifications } from "./notificationsData";
import NotificationCard from "./NotificationCard";
import NotificationDetail from "./NotificationDetail";
import { OptionsSheet, Toast } from "./NotificationsSheets";
import MobileModuleHeader, { MobileHeaderAction } from "../MobileModuleHeader";

export default function NotificationsMobile({
  notifications,
  onMenu,
  onBack,
  onOpenEntity,
  onMarkRead,
  onMarkAllRead,
  onDelete,
  onUndoDelete,
  onRefresh,
  onOpenSettings,           // ouvre les réglages de notifications (types + canaux)
  dark = false,             // applique le thème sombre (.enf--dark)
  sons: controlledSons,     // état du toggle « Sons » piloté par le conteneur
  onToggleSons: controlledToggleSons,
  presentation = "sheet",   // "sheet" | "center"
  grouping = "date",        // "date" | "type" | "none"
  density = "cosy",         // "cosy" | "compact"
  unreadStyle = "tint",     // "tint" | "dot" | "bar"
}) {
  const seed = notifications || getDemoNotifications();
  const [notifs, setNotifs] = useState(() => seed.map((n) => ({ ...n })));
  const [filter, setFilter] = useState("all");
  const [detailId, setDetailId] = useState(null);
  const [optsOpen, setOptsOpen] = useState(false);
  const [sonsLocal, setSonsLocal] = useState(true);
  const [toast, setToast] = useState(null);
  const toastT = useRef(null);
  const lastDeleted = useRef(null);

  // Toggle « Sons » : piloté par le conteneur si fourni, sinon état local (démo).
  const sons = controlledSons !== undefined ? controlledSons : sonsLocal;
  const toggleSons = controlledToggleSons || (() => setSonsLocal((s) => !s));

  // Resynchronise si la prop `notifications` change de référence.
  useEffect(() => {
    if (notifications) setNotifs(notifications.map((n) => ({ ...n })));
  }, [notifications]);

  const showToast = useCallback((msg, opts = {}) => {
    setToast({ msg, ...opts, id: Date.now() });
    clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToast(null), opts.undo ? 4200 : 2400);
  }, []);

  const unread = notifs.filter((n) => !n.read).length;
  const counts = useMemo(() => {
    const c = { all: notifs.length };
    notifs.forEach((n) => { c[n.type] = (c[n.type] || 0) + 1; });
    return c;
  }, [notifs]);

  const visible = filter === "all" ? notifs : notifs.filter((n) => n.type === filter);
  const detailNotif = notifs.find((n) => n.id === detailId) || null;

  const toggleRead = (n) => {
    const next = !n.read;
    setNotifs((l) => l.map((x) => (x.id === n.id ? { ...x, read: next } : x)));
    onMarkRead?.(n, next);
    showToast(next ? "Marquée comme lue" : "Marquée comme non lue");
  };
  // ouverture du modal : marque lu en silence si nécessaire
  const openDetail = (n) => {
    setDetailId(n.id);
    if (!n.read) {
      setNotifs((l) => l.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      onMarkRead?.(n, true);
    }
  };
  const markAll = () => {
    if (unread === 0) return;
    setNotifs((l) => l.map((x) => ({ ...x, read: true })));
    onMarkAllRead?.();
    showToast("Toutes les notifications sont lues");
  };
  const remove = (n) => {
    const idx = notifs.findIndex((x) => x.id === n.id);
    lastDeleted.current = { item: notifs[idx], idx };
    setNotifs((l) => l.map((x) => (x.id === n.id ? { ...x, removing: true } : x)));
    setTimeout(() => setNotifs((l) => l.filter((x) => x.id !== n.id)), 260);
    onDelete?.(n);
    showToast("Notification supprimée", { ic: "trash-2", undo: true });
  };
  const undo = () => {
    const d = lastDeleted.current;
    if (!d) return;
    setNotifs((l) => {
      const next = l.filter((x) => x.id !== d.item.id);
      next.splice(d.idx, 0, { ...d.item, removing: false });
      return next;
    });
    onUndoDelete?.(d.item);
    lastDeleted.current = null;
    setToast(null);
  };

  const groups = useMemo(() => {
    if (grouping === "none") return [{ key: "all", label: null, items: visible }];
    if (grouping === "type") {
      return FILTERS.filter((f) => f.key !== "all").map((f) => ({
        key: f.key, label: f.label, items: visible.filter((n) => n.type === f.key),
      })).filter((gr) => gr.items.length);
    }
    return BUCKETS.map((b) => ({
      key: b.key, label: b.label, items: visible.filter((n) => n.bucket === b.key),
    })).filter((gr) => gr.items.length);
  }, [grouping, visible]);

  return (
    <div className={"enf" + (dark ? " enf--dark" : "")}
      data-density={density === "compact" ? "compact" : "cosy"} data-ustyle={unreadStyle}>
      <MobileModuleHeader
        title="Notifications"
        subtitle={unread === 0 ? "Tout est à jour" : `${unread} non ${unread > 1 ? "lues" : "lue"}`}
        onMenu={onMenu}
        onLeading={onMenu ? undefined : onBack}
        leadingIcon="arrow-left"
        leadingLabel="Retour"
        actions={
          <>
            <MobileHeaderAction label="Tout marquer comme lu" onClick={markAll}>
              <Ico name="check-check" />
            </MobileHeaderAction>
            {onOpenSettings ? (
              <MobileHeaderAction label="Paramètres des notifications" onClick={onOpenSettings}>
                <Ico name="settings" />
              </MobileHeaderAction>
            ) : null}
            <MobileHeaderAction label="Options" onClick={() => setOptsOpen(true)}>
              <Ico name="more-horizontal" />
            </MobileHeaderAction>
          </>
        }
      >
        <div className="chips">
          {FILTERS.map((f) => (
            <button key={f.key} className={"chip" + (filter === f.key ? " on" : "")}
              onClick={() => setFilter(f.key)}>
              <Ico name={f.ic} />{f.label}
              <span className="chip__n">{counts[f.key] || 0}</span>
            </button>
          ))}
        </div>
      </MobileModuleHeader>

      {/* LISTE */}
      <div className="body">
        {visible.length === 0 ? (
          <div className="empty">
            <span className="eic"><Ico name="bell-off" /></span>
            <b>Aucune notification</b>
            <span>Tout est à jour de ce côté. Les nouvelles alertes apparaîtront ici.</span>
          </div>
        ) : (
          <div className="feed">
            {groups.map((gr) => (
              <React.Fragment key={gr.key}>
                {gr.label && (
                  <div className="feed__group">{gr.label}<span className="gn">{gr.items.length}</span></div>
                )}
                {gr.items.map((n) => (
                  <NotificationCard key={n.id} n={n}
                    onOpen={openDetail} onToggleRead={toggleRead} onDelete={remove} />
                ))}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <NotificationDetail n={detailNotif} mode={presentation} onClose={() => setDetailId(null)}
        onToggleRead={toggleRead} onDelete={remove} onOpenEntity={onOpenEntity} />
      <OptionsSheet open={optsOpen} onClose={() => setOptsOpen(false)}
        sons={sons} onToggleSons={toggleSons} onRefresh={onRefresh}
        onSettings={onOpenSettings} />
      <Toast data={toast} onUndo={undo} />
    </div>
  );
}
